// Reading the call restrictions errors (contract §4) out of RTK Query errors.
// Errors use the ApiError envelope {code, message, details}; RTK hands them
// over as {status, data} (or wrapped once more in {error} by some callers).

export const DESTINATION_NOT_AUTHORIZED = 'DESTINATION_NOT_AUTHORIZED';
export const CALL_RESTRICTIONS_LOCKED = 'CALL_RESTRICTIONS_LOCKED';
export const CALL_RESTRICTIONS_NUMBER_LOCKED = 'CALL_RESTRICTIONS_NUMBER_LOCKED';
export const PRICE_LIMIT_ABOVE_ALLOWED = 'PRICE_LIMIT_ABOVE_ALLOWED';

export interface ApiErrorBody {
    status?: number;
    code: string;
    message?: string;
    details?: Record<string, unknown>;
}

type Raw = Record<string, unknown>;

export const readApiError = (error: unknown): ApiErrorBody | undefined => {
    if (!error || typeof error !== 'object') return undefined;
    const outer = error as Raw;
    const candidate = (outer.data !== undefined ? outer : (outer.error as Raw | undefined)) ?? outer;
    const data = (candidate as Raw).data as Raw | undefined;
    if (!data || typeof data !== 'object' || typeof data.code !== 'string') return undefined;
    return {
        status: typeof (candidate as Raw).status === 'number' ? (candidate as Raw).status as number : undefined,
        code: data.code,
        message: typeof data.message === 'string' ? data.message : undefined,
        details: data.details && typeof data.details === 'object' ? data.details as Record<string, unknown> : undefined,
    };
};

export interface DestinationRefusal {
    source?: string;
    label?: string;
    /** E.164 digits without `+`, as the backend normalised them. */
    number?: string;
}

/** The 422 a forwarding save gets when the target isn't authorized. */
export const destinationRefusalOf = (error: unknown): DestinationRefusal | undefined => {
    const body = readApiError(error);
    if (body?.code !== DESTINATION_NOT_AUTHORIZED) return undefined;
    const details = body.details ?? {};
    const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
    return {
        source: text(details.source)?.toLowerCase(),
        label: text(details.label),
        number: text(details.number)?.replace(/\D/g, '') || undefined,
    };
};

export const isCallRestrictionsLocked = (error: unknown): boolean =>
    readApiError(error)?.code === CALL_RESTRICTIONS_LOCKED;

export const isNumberLocked = (error: unknown): boolean =>
    readApiError(error)?.code === CALL_RESTRICTIONS_NUMBER_LOCKED;

export const NUMBER_LOCKED_MESSAGE = 'PhoneHQ has locked restrictions for this number.';
export const NUMBER_REJECTED_MESSAGE = 'We couldn’t save this number. Check it and try again.';

export const numberErrorMessage = (error: unknown): string | undefined => {
    const body = readApiError(error);
    if (body?.code === CALL_RESTRICTIONS_NUMBER_LOCKED) return NUMBER_LOCKED_MESSAGE;
    if (body?.status === 400) return body.message?.trim() || NUMBER_REJECTED_MESSAGE;
    return undefined;
};

/** PRICE_LIMIT_ABOVE_ALLOWED details: `{allowedMaximum, currency}` (PriceLimitAboveAllowedException). */
export const priceLimitAboveOf = (error: unknown): {allowedMaximum: number | null; currency?: string} | undefined => {
    const body = readApiError(error);
    if (body?.code !== PRICE_LIMIT_ABOVE_ALLOWED) return undefined;
    const value = Number(body.details?.allowedMaximum);
    const currency = body.details?.currency;
    return {
        allowedMaximum: body.details?.allowedMaximum === undefined || !Number.isFinite(value) ? null : value,
        currency: typeof currency === 'string' ? currency : undefined,
    };
};

const ASK = 'Choose another number or ask your admin.';

/** Inline message under a forwarding number field (board ForwardingBlocked). */
export const forwardingRefusalMessage = (refusal: DestinationRefusal): string => {
    if (refusal.source === 'price_limit' && refusal.label) {
        return `Forwarding to this number isn't authorized. This number ${refusal.label}. ${ASK}`;
    }
    if (refusal.label) {
        return `Forwarding to this number isn't authorized (${refusal.label}). ${ASK}`;
    }
    return `Forwarding to this number isn't authorized. ${ASK}`;
};

/** Digits only, so `+48 22…`, `4822…` and numbers compare equal. */
export const sameNumber = (a: unknown, b: unknown): boolean => {
    const digits = (value: unknown) => String(value ?? '').replace(/\D/g, '');
    return digits(a) !== '' && digits(a) === digits(b);
};

export const digitsOf = (value: unknown) => String(value ?? '').replace(/\D/g, '');

/**
 * Which forwarding targets a refusal is about. `targets` maps each active
 * target (a line's working-hours forward, an IVR option…) to its number.
 * The refusal names the number it refused; without one, or if no target
 * matches it, every active target is treated as refused. Returns the refused
 * digits per target, to compare against later edits.
 */
export const refusedTargets = <K extends string>(
    refusal: DestinationRefusal,
    targets: Partial<Record<K, unknown>>,
): Partial<Record<K, string>> => {
    const entries = (Object.entries(targets) as [K, unknown][]).filter(([, number]) => digitsOf(number) !== '');
    const matching = refusal.number ? entries.filter(([, number]) => sameNumber(number, refusal.number)) : [];
    const refused = matching.length ? matching : entries;
    return Object.fromEntries(refused.map(([key, number]) => [key, digitsOf(number)])) as Partial<Record<K, string>>;
};

/** Targets that still point at the refused number: Save stays disabled until each one changes. */
export const stillRefused = <K extends string>(
    refused: Partial<Record<K, string>>,
    targets: Partial<Record<K, unknown>>,
): K[] =>
    (Object.keys(refused) as K[]).filter((key) => digitsOf(targets[key]) !== '' && digitsOf(targets[key]) === refused[key]);

export type RefusalOutcome<K extends string> =
    | {refused: Partial<Record<K, string>>; toast?: undefined}
    | {refused?: undefined; toast: string};

export const refusalOutcome = <K extends string>(
    refusal: DestinationRefusal,
    targets: Partial<Record<K, unknown>>,
): RefusalOutcome<K> => {
    const refused = refusedTargets(refusal, targets);
    return Object.keys(refused).length ? {refused} : {toast: forwardingRefusalMessage(refusal)};
};
